import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of } from 'rxjs';
import Swal from 'sweetalert2';
import { FormsModule } from '@angular/forms';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-institucion',
  standalone: true,
  imports: [FormsModule, CommonModule, ReactiveFormsModule],
  templateUrl: './institucion.component.html',
  styleUrl: './institucion.component.css',
})
export class InstitucionComponent implements OnInit {
  institucionForm!: FormGroup;
  id_institucion_educativa!: number;
  isEditable = false;
  showPassword = false;
  claveOriginal: string = '';
  fieldOrder = [
    'cod_mod',
    'cen_edu',
    'd_niv_mod',
    'tipssexo',
    'd_tipssexo',
    'gestion',
    'd_gestion',
    'ges_dep',
    'd_ges_dep',
    'director',
    'telefono',
    'email',
    'codcp_inei',
    'codccpp',
    'cen_pob',
    'area_censo',
    'dareacenso',
    'codgeo',
    'd_dpto',
    'd_prov',
    'd_dist',
    'clave',
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.institucionForm = this.fb.group({
      clave: [{ disabled: true }],
      cod_mod: [{ disabled: true }],
      cen_edu: [{ disabled: true }],
      d_niv_mod: [{ disabled: true }],
      tipssexo: [{ disabled: true }],
      d_tipssexo: [{ disabled: true }],
      gestion: [{ disabled: true }],
      d_gestion: [{ disabled: true }],
      ges_dep: [{ disabled: true }],
      d_ges_dep: [{ disabled: true }],
      director: [{ disabled: true }],
      telefono: [{ disabled: true }, [Validators.pattern(/^\d{6,9}$/)]],
      email: [{ disabled: true }, [Validators.email]],
      codcp_inei: [{ disabled: true }],
      codccpp: [{ disabled: true }],
      cen_pob: [{ disabled: true }],
      area_censo: [{ disabled: true }],
      dareacenso: [{ disabled: true }],
      codgeo: [{ disabled: true }],
      d_dpto: [{ disabled: true }],
      d_prov: [{ disabled: true }],
      d_dist: [{ disabled: true }],
    });

    this.route.queryParams.subscribe((params) => {
      this.id_institucion_educativa = params['id_institucion_educativa'];
    });

    if (!this.id_institucion_educativa) {
      Swal.fire('Error', 'Institución no válida.', 'error').then(() => {
        this.router.navigate(['/instituciones']);
      });
      return;
    }

    this.cargarInstitucion();
  }

  cargarInstitucion(): void {
    this.http
      .get(
        environment.apiUrl +
          `/institucion-educativa/${this.id_institucion_educativa}`
      )
      .subscribe(
        (response: any) => {
          if (response) {
            this.claveOriginal = response.clave;
            this.institucionForm.patchValue({
              cod_mod: response.cod_mod,
              cen_edu: response.cen_edu,
              d_niv_mod: response.d_niv_mod,
              tipssexo: response.tipssexo,
              d_tipssexo: response.d_tipssexo,
              gestion: response.gestion,
              d_gestion: response.d_gestion,
              ges_dep: response.ges_dep,
              d_ges_dep: response.d_ges_dep,
              director: response.director,
              telefono: response.telefono,
              email: response.email,
              codcp_inei: response.codcp_inei,
              codccpp: response.codccpp,
              cen_pob: response.cen_pob,
              area_censo: response.area_censo,
              dareacenso: response.dareacenso,
              codgeo: response.codgeo,
              d_dpto: response.d_dpto,
              d_prov: response.d_prov,
              d_dist: response.d_dist,
              clave: '',
            });
          }
        },
        (error) => {
          Swal.fire(
            'Error',
            error.error.message || 'No se pudo obtener la institución.',
            'error'
          ).then(() => {
            this.router.navigate(['/instituciones']);
          });
        }
      );
  }

  habilitarEdicion(): void {
    this.isEditable = true;
    this.institucionForm.enable();
  }

  actualizarInstitucion(): void {
    if (this.institucionForm.invalid) {
      Swal.fire('Advertencia', 'Hay errores en el formulario.', 'warning');
      return;
    }
    const nuevaClave = this.institucionForm.value.clave;

    Swal.fire({
      title: '¿Estás seguro?',
      text: '¿Quieres actualizar la institución?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, actualizar',
      cancelButtonText: 'No, cancelar',
    }).then((result) => {
      if (result.isConfirmed) {
        const formData = this.institucionForm.value;

        const data: any = Object.fromEntries(
          Object.entries(formData).filter(
            ([key, value]) =>
              value !== '' && value !== null && value !== undefined
          )
        );

        if (!nuevaClave) {
          delete data.clave;
        }

        //console.log(data);
        this.http
          .patch(
            environment.apiUrl +
              `/institucion-educativa/${this.id_institucion_educativa}`,
            data
          )
          .pipe(
            catchError((error) => {
              //alert(error.error.message);
              Swal.fire({
                title: 'Error',
                text:
                  error.error.message ||
                  'No se pudo actualizar la institución.',
                icon: 'error',
              });
              return of(null);
            })
          )
          .subscribe((response) => {
            if (response) {
              Swal.fire(
                'Éxito',
                'Institución actualizada correctamente.',
                'success'
              ).then(() => {
                this.router.navigate(['/instituciones']);
              });
            }
          });
      }
    });
  }

  volver(): void {
    if (this.institucionForm.dirty && !this.isEditable) {
      Swal.fire({
        title: 'Cambios no guardados',
        text: '¿Estás seguro de salir sin guardar los cambios?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, salir',
        cancelButtonText: 'No, cancelar',
      }).then((result) => {
        if (result.isConfirmed) {
          this.router.navigate(['/instituciones']);
        }
      });
    } else {
      this.router.navigate(['/instituciones']);
    }
  }

  formatLabel(key: string): string {
    return key.toUpperCase();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }
}
